// src/pages/Staff.js
import { useEffect, useState } from 'react';
import { collection, onSnapshot, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { PageHeader, Card, Badge, Btn, Table, TD, SearchBar, FilterSelect, ConfirmModal } from '../components/UI';

const ROLES = ['all', 'Guard', 'TT (Ticket Checker)', 'Driver', 'Assistant Driver', 'Station Master'];

export default function Staff() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [confirm, setConfirm] = useState(null);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'staff'), snap => {
      setStaff(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, []);

  const updateStatus = async (id, status) => {
    await updateDoc(doc(db, 'staff', id), { status });
    setConfirm(null);
  };

  const filtered = staff.filter(s => {
    const q = search.toLowerCase();
    const matchQ = !q || s.name?.toLowerCase().includes(q) || s.phone?.includes(q) || s.email?.toLowerCase().includes(q) || s.assignedTrainName?.toLowerCase().includes(q) || String(s.assignedTrainID).includes(q);
    const matchS = statusFilter === 'all' || s.status === statusFilter;
    const matchR = roleFilter === 'all' || s.role === roleFilter;
    return matchQ && matchS && matchR;
  });

  const exportCSV = () => {
    const rows = [['Name','Phone','Email','Role','Train','Status']];
    filtered.forEach(s => rows.push([s.name||'',s.phone||'',s.email||'',s.role||'',`${s.assignedTrainName||''} #${s.assignedTrainID||''}`,s.status||'']));
    const csv = rows.map(r => r.join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = 'staff.csv'; a.click();
  };

  const counts = {
    all: staff.length,
    pending: staff.filter(s => s.status === 'pending').length,
    approved: staff.filter(s => s.status === 'approved').length,
    blocked: staff.filter(s => s.status === 'blocked').length,
  };

  const rows = filtered.map(s => [
    <TD key="nm"><div style={{ fontWeight: 600 }}>{s.name}</div><div style={{ color: '#646970', fontSize: 11 }}>{s.email}</div></TD>,
    <TD key="ph" mono>{s.phone || '—'}</TD>,
    <TD key="rl"><span style={{ background: '#2a2a2a', border: '1px solid #3a3a3a', borderRadius: 3, padding: '2px 8px', fontSize: 11 }}>{s.role}</span></TD>,
    <TD key="tr"><div style={{ color: '#f0f0f1' }}>{s.assignedTrainName}</div><div style={{ color: '#646970', fontSize: 11 }}>#{s.assignedTrainID}</div></TD>,
    <TD key="st"><Badge status={s.status} /></TD>,
    <TD key="dt">{s.createdAt ? new Date(s.createdAt?.toDate?.() || s.createdAt).toLocaleDateString('bn-BD') : '—'}</TD>,
    <TD key="ac">
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {/* pending → approve */}
        {s.status === 'pending' && (
          <Btn variant="success" onClick={() => setConfirm({ id: s.id, status: 'approved', name: s.name, action: 'approve' })}>
            ✓ Approve
          </Btn>
        )}
        {/* approved → pending এ ফেরত */}
        {s.status === 'approved' && (
          <Btn variant="ghost" onClick={() => setConfirm({ id: s.id, status: 'pending', name: s.name, action: 'pending' })}>
            ↺ Pending
          </Btn>
        )}
        {/* block (pending বা approved যেকোনো থেকে) */}
        {s.status !== 'blocked' && (
          <Btn variant="danger" onClick={() => setConfirm({ id: s.id, status: 'blocked', name: s.name, action: 'block' })}>
            ⊘ Block
          </Btn>
        )}
        {/* unblock → approved এ ফেরত */}
        {s.status === 'blocked' && (
          <Btn variant="ghost" onClick={() => setConfirm({ id: s.id, status: 'approved', name: s.name, action: 'unblock' })}>
            ↩ Unblock
          </Btn>
        )}
      </div>
    </TD>,
  ]);

  return (
    <div>
      <PageHeader
        title="🪪 Staff Management"
        sub={`মোট ${staff.length} জন staff · ${counts.pending} জন pending`}
        action={<Btn onClick={exportCSV} variant="ghost" size="sm">⬇ CSV Export</Btn>}
      />

      {/* Status tabs */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
        {[['all','সকল'],['pending','Pending'],['approved','Approved'],['blocked','Blocked']].map(([v,l]) => (
          <button key={v} onClick={() => setStatusFilter(v)} style={{
            padding: '4px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer',
            background: statusFilter === v ? '#2271b1' : '#2a2a2a',
            color: statusFilter === v ? '#fff' : '#a7aaad',
            border: statusFilter === v ? '1px solid #2271b1' : '1px solid #3a3a3a',
          }}>{l} ({counts[v] || staff.filter(s => v === 'all' || s.status === v).length})</button>
        ))}
      </div>

      <Card>
        <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          <SearchBar value={search} onChange={setSearch} placeholder="নাম, ফোন, ট্রেন..." />
          <FilterSelect value={statusFilter} onChange={setStatusFilter} options={[['all','সকল Status'],['pending','Pending'],['approved','Approved'],['blocked','Blocked']]} />
          <FilterSelect value={roleFilter} onChange={setRoleFilter} options={ROLES.map(r => [r, r === 'all' ? 'সকল Role' : r])} />
          <span style={{ color: '#646970', fontSize: 12, marginLeft: 'auto' }}>{filtered.length} টি result</span>
        </div>
        <Table cols={['নাম', 'ফোন', 'পদবি', 'ট্রেন', 'Status', 'তারিখ', 'Action']} rows={rows} loading={loading} />
      </Card>

      <ConfirmModal
        open={!!confirm}
        title={
          confirm?.action === 'approve' ? '✓ Approve করবেন?' :
          confirm?.action === 'pending' ? '↺ Pending করবেন?' :
          confirm?.action === 'block'   ? '⊘ Block করবেন?' :
                                          '↩ Unblock করবেন?'
        }
        msg={`"${confirm?.name}" কে ${
          confirm?.action === 'approve' ? 'approve' :
          confirm?.action === 'pending' ? 'pending' :
          confirm?.action === 'block'   ? 'block' :
                                          'unblock'
        } করতে চান?`}
        onConfirm={() => updateStatus(confirm.id, confirm.status)}
        onCancel={() => setConfirm(null)}
        danger={confirm?.action === 'block'}
      />
    </div>
  );
}